import { useState } from "react";
import {
  X,
  Building2,
  CreditCard,
  Mail,
  ExternalLink,
  Copy,
  Check,
  CircleDot,
} from "lucide-react";
import Button from "../../../components/Button";
import { ShowToast } from "../../../components/toast";
import {
  useSetCommissionRate,
  useSetAddonConfig,
  useReconcileBilling,
  useSyncFromStripe,
} from "../hooks";
import {
  BillingReconciliation,
  ClientBilling,
  isDashboardVisibilityAddon,
  STATUS_COLORS,
  STATUS_LABELS,
  SUBSCRIPTION_STATUS_COLORS,
} from "../types";
import { updateEmailMarketingEnabled } from "../../../helpers/api/crm";
import {
  AddonDraft,
  BillingConfigDraft,
  addonDraftChanged,
  draftFrom,
  ratePayloadFrom,
  validateDraft,
} from "./client-detail/billing-config-draft";
import { BillingConfigCard } from "./client-detail/BillingConfigCard";
import { StripeSubscriptionsCard } from "./client-detail/StripeSubscriptionsCard";
import { Card, CardRow } from "./client-detail/primitives";

interface ClientDetailModalProps {
  client: ClientBilling;
  onClose: () => void;
}

export function ClientDetailModal({ client, onClose }: ClientDetailModalProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [draft, setDraft] = useState<BillingConfigDraft>(() => draftFrom(client));
  const patchDraft = (patch: Partial<BillingConfigDraft>) =>
    setDraft((d) => ({ ...d, ...patch }));
  const patchAddon = (type: string, patch: Partial<AddonDraft>) =>
    setDraft((d) => ({
      ...d,
      addons: d.addons.map((a) => (a.type === type ? { ...a, ...patch } : a)),
    }));

  const setRateMutation = useSetCommissionRate();
  const setAddonMutation = useSetAddonConfig();
  const reconcileMutation = useReconcileBilling();
  const syncMutation = useSyncFromStripe();
  const [reconciliation, setReconciliation] = useState<BillingReconciliation | null>(null);
  const isSaving = setRateMutation.isPending || setAddonMutation.isPending;

  // The add-on whose enablement is awaiting the dashboard-visibility prompt
  // (only set for dashboard-coupled add-ons like email_marketing).
  const [pendingDashboardAddonType, setPendingDashboardAddonType] = useState<
    string | null
  >(null);
  const [flippingDashboard, setFlippingDashboard] = useState(false);

  const changedAddonDrafts = draft.addons.filter((a) =>
    addonDraftChanged(a, client.addons),
  );

  // A dashboard-coupled add-on (email_marketing) being newly enabled while the
  // customer's dashboard product is off triggers the dashboard-visibility
  // prompt. Returns the first such draft, or null when none needs the prompt.
  const findDashboardPromptAddon = (): AddonDraft | null => {
    if (client.dashboardEmailMarketingEnabled) return null;
    return (
      changedAddonDrafts.find(
        (d) => d.enabled && isDashboardVisibilityAddon(d.type),
      ) ?? null
    );
  };

  // The rate write and the add-on writes are separate endpoints behind one
  // "Save Changes" button, so they must be sequenced: await the rate save (with
  // its own toast suppressed), then fire one add-on mutation per changed add-on,
  // and show one combined success toast. Firing them in parallel would create
  // partial-save states and double toasts.
  const handleSave = async (opts?: { skipDashboardPrompt?: boolean }) => {
    const error = validateDraft(draft, changedAddonDrafts);
    if (error) {
      ShowToast({ type: "error", message: error });
      return;
    }

    // When enabling a dashboard-coupled add-on for a client whose dashboard
    // doesn't yet show it, prompt the admin to also turn on dashboard visibility
    // (hotel.emailMarketingEnabled) before saving. The prompt's buttons re-invoke
    // handleSave with skipDashboardPrompt so the save proceeds.
    if (!opts?.skipDashboardPrompt) {
      const promptAddon = findDashboardPromptAddon();
      if (promptAddon) {
        setPendingDashboardAddonType(promptAddon.type);
        return;
      }
    }

    const ratePayload = ratePayloadFrom(draft, client);

    try {
      await setRateMutation.mutateAsync(ratePayload);

      for (const addon of changedAddonDrafts) {
        const trimmedPrice = addon.monthlyPrice.trim();
        await setAddonMutation.mutateAsync({
          userId: client.userId,
          addonType: addon.type,
          enabled: addon.enabled,
          trialEnabled: addon.trialEnabled,
          // Omit price (rather than send 0) when there is none — the backend
          // contract is "omit the field" to leave the persisted price.
          ...(trimmedPrice !== "" ? { monthlyPrice: parseFloat(trimmedPrice) } : {}),
          ...(addon.startDate ? { startDate: addon.startDate } : {}),
          _silentToast: true,
        });
      }

      ShowToast({ type: "success", message: "Billing config updated" });
      setIsEditing(false);
      // A manual rate edit makes any prior reconciliation panel stale.
      setReconciliation(null);
    } catch {
      // Both mutations surface their own error toast via onError; nothing to add.
    }
  };

  // Modal: "Enable" flips dashboard visibility (hotel.emailMarketingEnabled) via
  // the CRM endpoint, then resumes the billing save. "Cancel" saves billing only
  // (the add-on still bills; the dashboard link just stays hidden).
  const handleEnableDashboardThenSave = async () => {
    setFlippingDashboard(true);
    try {
      await updateEmailMarketingEnabled(client.userId, true);
    } catch {
      ShowToast({
        type: "error",
        message: "Couldn't enable Email Marketing on the customer's dashboard",
      });
      setFlippingDashboard(false);
      return;
    }
    setFlippingDashboard(false);
    setPendingDashboardAddonType(null);
    await handleSave({ skipDashboardPrompt: true });
  };

  const handleSkipDashboardThenSave = async () => {
    setPendingDashboardAddonType(null);
    await handleSave({ skipDashboardPrompt: true });
  };

  const handleSync = () => {
    reconcileMutation.mutate(client.userId, {
      onSuccess: (result) => setReconciliation(result),
    });
  };

  const handleApplyStripeRate = () => {
    syncMutation.mutate(client.userId, {
      onSuccess: (result) => setReconciliation(result.reconciliation),
    });
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setReconciliation(null);
    setDraft(draftFrom(client));
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  const effectiveStatus =
    client.invoiceStatus === "not_configured" && client.commissionRate > 0
      ? ("pending" as const)
      : client.invoiceStatus;

  const pm = client.paymentMethod;
  const address = client.billingAddress;
  const displayName = client.clientName || client.hotelName || client.email;
  const initials = displayName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-surface-primary rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with client identity */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3.5">
              <div className="w-12 h-12 rounded-full bg-[#3d828f]/10 flex items-center justify-center shrink-0">
                <span className="text-base font-semibold text-primary">
                  {initials}
                </span>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-content-primary leading-tight">
                    {displayName}
                  </h2>
                  <div
                    className={`${STATUS_COLORS[effectiveStatus]} text-[0.6875rem] font-medium border rounded-full py-0.5 px-2`}
                  >
                    {STATUS_LABELS[effectiveStatus]}
                  </div>
                </div>
                <p className="text-sm text-content-tertiary mt-0.5">
                  {client.email}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 -mr-1.5 -mt-1.5 rounded-lg hover:bg-surface-secondary transition-colors"
            >
              <X className="w-4.5 h-4.5 text-content-tertiary" />
            </button>
          </div>
        </div>

        <div className="border-t border-border-secondary" />

        {/* Scrollable content */}
        <div className="overflow-y-auto px-6 py-5 space-y-4 flex-1">
          {/* Hotel & Location card */}
          <Card>
            <CardRow
              icon={<Building2 className="w-4 h-4 text-content-tertiary" />}
              label="Hotel"
              value={client.hotelName || "—"}
            />
            {address &&
              (address.city || address.state) && (
                <CardRow
                  label="Location"
                  value={
                    [address.city, address.state]
                      .filter(Boolean)
                      .join(", ")
                  }
                  indent
                />
              )}
          </Card>

          {/* Stripe & Payment card */}
          <Card>
            <CardRow
              icon={<CreditCard className="w-4 h-4 text-content-tertiary" />}
              label="Stripe Customer"
              value={
                client.stripeCustomerId ? (
                  <span className="flex items-center gap-1.5">
                    <span className="font-mono text-xs text-content-secondary">
                      {client.stripeCustomerId}
                    </span>
                    <button
                      onClick={() =>
                        copyToClipboard(
                          client.stripeCustomerId!,
                          "stripeId",
                        )
                      }
                      className="p-0.5 rounded hover:bg-surface-hover transition-colors"
                      title="Copy Stripe ID"
                    >
                      {copiedField === "stripeId" ? (
                        <Check className="w-3 h-3 text-success" />
                      ) : (
                        <Copy className="w-3 h-3 text-content-tertiary" />
                      )}
                    </button>
                    <a
                      href={`https://dashboard.stripe.com/customers/${client.stripeCustomerId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-0.5 rounded hover:bg-surface-hover transition-colors"
                      title="Open in Stripe"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="w-3 h-3 text-content-tertiary" />
                    </a>
                  </span>
                ) : (
                  <span className="text-content-tertiary">Not linked</span>
                )
              }
            />
            <CardRow
              icon={<Mail className="w-4 h-4 text-content-tertiary" />}
              label="Billing Email"
              value={client.stripeEmail || client.email || "—"}
            />
            {pm ? (
              <CardRow
                icon={<CreditCard className="w-4 h-4 text-content-tertiary" />}
                label="Card"
                value={
                  <span className="flex items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-surface-tertiary rounded text-xs font-medium text-content-secondary capitalize">
                      {pm.brand}
                    </span>
                    <span className="text-content-secondary">
                      •••• {pm.last4}
                    </span>
                    <span className="text-content-tertiary text-xs">
                      {pm.expMonth}/{pm.expYear}
                    </span>
                  </span>
                }
              />
            ) : (
              <CardRow
                icon={<CreditCard className="w-4 h-4 text-content-tertiary" />}
                label="Card"
                value={
                  <span className="text-warning-dark text-xs font-medium">
                    No payment method
                  </span>
                }
              />
            )}
            {client.subscriptionStatus && (
              <CardRow
                icon={<CircleDot className="w-4 h-4 text-content-tertiary" />}
                label="Subscription"
                value={
                  <span className={`${SUBSCRIPTION_STATUS_COLORS[client.subscriptionStatus] || "bg-gray-100 text-gray-800 border-gray-300"} text-[0.6875rem] font-medium border rounded-full py-0.5 px-2 capitalize`}>
                    {client.subscriptionStatus.replace("_", " ")}
                  </span>
                }
              />
            )}
          </Card>

          <BillingConfigCard
            client={client}
            isEditing={isEditing}
            draft={draft}
            onDraftChange={patchDraft}
            onAddonChange={patchAddon}
            isSaving={isSaving}
            onEdit={() => setIsEditing(true)}
            onSave={() => handleSave()}
            onCancel={cancelEditing}
            reconciliation={reconciliation}
            onSync={handleSync}
            isSyncing={reconcileMutation.isPending}
            onApplyStripeRate={handleApplyStripeRate}
            isApplying={syncMutation.isPending}
          />

          <StripeSubscriptionsCard userId={client.userId} />
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border-secondary flex justify-end">
          <Button
            variant="outline"
            onClick={onClose}
            className="!py-2 !px-5 !text-sm !rounded-lg"
          >
            Close
          </Button>
        </div>

        {/* Prompt: also surface Email Marketing on the customer's dashboard */}
        {pendingDashboardAddonType && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50"
            onClick={(e) => {
              e.stopPropagation();
              if (!flippingDashboard) setPendingDashboardAddonType(null);
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Enable Email Marketing on their dashboard"
              className="bg-surface-primary rounded-2xl shadow-2xl w-full max-w-md p-6 m-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-base font-semibold text-content-primary mb-2">
                Enable Email Marketing on their dashboard?
              </h3>
              <p className="text-sm text-content-tertiary mb-5">
                {displayName} doesn't have Email Marketing enabled on their
                dashboard yet. Enabling adds the Email Marketing link to their
                dashboard. You can skip this and still bill the add-on.
              </p>
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={handleSkipDashboardThenSave}
                  disabled={flippingDashboard}
                  className="py-2 px-4 text-sm text-content-tertiary hover:text-content-primary hover:bg-surface-secondary rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <Button
                  onClick={handleEnableDashboardThenSave}
                  disabled={flippingDashboard}
                  className="!py-2 !px-5 !text-sm !rounded-lg"
                >
                  {flippingDashboard ? "Enabling..." : "Enable"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
